import React, { useEffect } from 'react'
import {
  HashRouter,
  Route,
  Routes,
  useLocation,
  useNavigate
} from 'react-router-dom'
import { FetchDataComponent } from './data/FetchDataComponent'
import LandingPage from './landing/LandingPage'
import Login from './login/Login'
import Profile from './user/Profile'
import ForgotPassword from './forgot-password/ForgotPassword'
import UserDetails from './user/UserDetails'
import ChangePassword from './user/ChangePassword'
import { useSelector } from 'react-redux'
import { State } from './types/state'
import { AxiosConfigContainer } from './requests/AxiosConfigContainer'
import { ToastProvider } from './toast/Toast'
import LoadingSpinnerProvider from './loading-spinner/LoadingSpinner'
import { HeaderMobile, HeaderNormal } from './header/Header'
import { useIsMobileView } from './hooks/useIsMobileView'
import Users from './users/Users'
import ConfigurationPanel from './app-state/ConfigurationPanel'
import Groups from './groups/Groups'
import { Role } from './types/user'
import Subjects from './subjects/Subjects'
import SubjectGroups from './subject-groups/SubjectGroups'
import SubjectsSelection from './subject-selection/SubjectsSelection'
import StudentSelections from './users/UserSelections'
import UserGroups from './groups/UserGroups'
import SpecificUserGroup from './groups/SpecificUserGroup'
import SubjectRequirements from './subjects/SubjectRequirements'
import CommentList from './comments/CommentsList'
import SubjectsStatistics from './users/SubjectsStats'
import CommentedList from './comments/CommentedList'
// import Statistics from './statistics/Statistics'

const App = (): JSX.Element => {
  const { username } = useSelector((state: State) => state.user)

  return (
    <>
      <HashRouter>
        <AxiosConfigContainer />
        <ToastProvider />
        <LoadingSpinnerProvider />
        <FetchDataComponent />
        <Router username={username} />
      </HashRouter>
    </>
  )
}

interface RouterProps {
  username: string | null
}

const Router = ({ username }: RouterProps): JSX.Element => {
  const { isUserStateFetched } = useSelector((state: State) => state.refresh)
  const location = useLocation()
  const navigate = useNavigate()
  const isMobile = useIsMobileView()

  useEffect(() => {
    if (
      isUserStateFetched &&
      username === null &&
      !location.pathname.includes('login') &&
      !location.pathname.includes('register') &&
      !location.pathname.includes('forgot-password')
    ) {
      navigate('/login')
    }
  }, [location, navigate, username, isUserStateFetched])

  return isMobile ? (
    <HeaderMobile>
      <Routes>
        <Route path='/' element={<LandingPage />} />
        <Route path='/login' element={<Login />} />
        <Route path='/forgot-password' element={<ForgotPassword />} />
        {username !== null && (
          <>
            <Route path='/profile' element={<Profile />} />
            <Route path='/comments' element={<CommentList />} />
            <Route path='/commented' element={<CommentedList />} />
            <Route path='/users' element={<Users />} />
            <Route path='/subjects' element={<Subjects />} />
            <Route path='/subject-groups' element={<SubjectGroups />} />
            {/* <Route path='/statistics' element={<Statistics />} /> */}
            <Route
              path='/subject-statistics'
              element={<SubjectsStatistics />}
            />
            <Route path='/subjects-selection' element={<SubjectsSelection />} />
            <Route path='/student-selections' element={<StudentSelections />} />
            <Route path='/user-groups' element={<UserGroups />} />
            <Route
              path='/user-groups/:groupId'
              element={<SpecificUserGroup />}
            />
            <Route
              path='/subjects/:subjectId'
              element={<SubjectRequirements />}
            />
            <Route
              path='/groups'
              element={
                <Groups
                  caregiverRoles={[Role.TEACHER, Role.ADMIN, Role.SUPERADMIN]}
                />
              }
            />
            <Route
              path='/parents'
              element={<Groups caregiverRoles={[Role.PARENT]} />}
            />
            <Route path='/user-details' element={<UserDetails />} />
            <Route path='/change-password' element={<ChangePassword />} />
            <Route path='/configuration' element={<ConfigurationPanel />} />
          </>
        )}
      </Routes>
    </HeaderMobile>
  ) : (
    <HeaderNormal>
      <Routes>
        <Route path='/' element={<LandingPage />} />
        <Route path='/login' element={<Login />} />
        <Route path='/forgot-password' element={<ForgotPassword />} />
        {username !== null && (
          <>
            <Route path='/profile' element={<Profile />} />
            <Route path='/comments' element={<CommentList />} />
            <Route path='/commented' element={<CommentedList />} />
            <Route path='/users' element={<Users />} />
            <Route path='/subjects' element={<Subjects />} />
            <Route path='/subject-groups' element={<SubjectGroups />} />
            {/* <Route path='/statistics' element={<Statistics />} /> */}
            <Route
              path='/subject-statistics'
              element={<SubjectsStatistics />}
            />
            <Route path='/subjects-selection' element={<SubjectsSelection />} />
            <Route path='/student-selections' element={<StudentSelections />} />
            <Route path='/user-groups' element={<UserGroups />} />
            <Route
              path='/user-groups/:groupId'
              element={<SpecificUserGroup />}
            />
            <Route
              path='/subjects/:subjectId'
              element={<SubjectRequirements />}
            />
            <Route
              path='/groups'
              element={
                <Groups
                  caregiverRoles={[Role.TEACHER, Role.ADMIN, Role.SUPERADMIN]}
                />
              }
            />
            <Route
              path='/parents'
              element={<Groups caregiverRoles={[Role.PARENT]} />}
            />
            <Route path='/user-details' element={<UserDetails />} />
            <Route path='/change-password' element={<ChangePassword />} />
            <Route path='/configuration' element={<ConfigurationPanel />} />
          </>
        )}
      </Routes>
    </HeaderNormal>
  )
}

export default App
